import {GetStaticPaths, GetStaticProps} from 'next';
import {FormattedMessage} from 'react-intl';

import {GridItem, GridTitle} from 'src/components/LinkGridLayout';
import {Layout} from 'src/components/layout';
import {LayoutProps} from 'src/components/layout/Layout';
import {ProductLinkGridSection} from 'src/components/layout/ProductLinkGridSection';
import {regionalConfigsArr} from 'src/config';
import {PageParams} from 'src/types';
import {
  getImageSourceFromAsset,
  IProductCollection,
} from 'src/utils/contentful';
import {contentfulApi} from 'src/utils/contentful/client';

interface CollectionsIndexPageProps extends Omit<LayoutProps, 'seo'> {
  collections: IProductCollection[];
}

export const getStaticPaths: GetStaticPaths = async () => {
  const paths = regionalConfigsArr.reduce((accumulatedPaths, config) => {
    const {locales, regionCode: region} = config;
    const localizedRegionalPaths = locales.map((locale) => ({
      params: {region, locale},
    }));
    return [...accumulatedPaths, ...localizedRegionalPaths];
  }, [] as {params: PageParams}[]);

  return {paths, fallback: false};
};

export const getStaticProps: GetStaticProps<
  CollectionsIndexPageProps,
  PageParams
> = async ({params, preview}) => {
  if (!params) {
    throw new Error(`Could not find valid params for collections index page`);
  }

  const {region, locale} = params;

  const api = preview ? contentfulApi.preview : contentfulApi;
  const {collections} = await api.getProductCollections({
    locale,
  });

  return {
    props: {
      collections,
      pageContext: {
        region,
        locale,
      },
      preview: !!preview,
    },
  };
};

// this is extracted to a component because we don't have access to the intl context in the page component
const Title = () => {
  return (
    <GridTitle>
      <FormattedMessage
        id="CollectionsIndex.title"
        defaultMessage="Collections"
      />
    </GridTitle>
  );
};

const CollectionsPage: React.FC<
  React.PropsWithChildren<CollectionsIndexPageProps>
> = (props) => {
  const {collections, pageContext, preview} = props;

  const collectionItems: GridItem[] = collections.map((collection) => {
    const {
      fields: {name, image: collectionImage, slug, products},
    } = collection;

    const {
      fields: {images: variantImages, previewImage},
    } = products[0].fields.variants[0];

    const backgroundImg = collectionImage
      ? collectionImage
      : variantImages
      ? variantImages[0]
      : previewImage;

    return {
      key: collection.sys.id,
      title: name,
      backgroundImageSrc: getImageSourceFromAsset(backgroundImg),
      linkTo: `/collections/${slug}`,
    };
  });

  return (
    <Layout
      seo={{
        title: 'Collections', // TODO Localize these strings
        description: 'Stryd product collections', // TODO Localize these strings
        includeInRegions: ['all'],
        slug: 'collections',
        slugPrefix: '',
        noIndex: false,
      }}
      preview={preview}
      pageContext={pageContext}
    >
      <ProductLinkGridSection title={<Title />} items={collectionItems} />
    </Layout>
  );
};

export default CollectionsPage;
